import { useCallback, memo, useMemo, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Box, SxProps, TableContainerProps } from "@mui/material";
import { useConfig } from "../../../contexts/ConfigContext";
import DataGridView from "./DataGridView";
import DataListView from "./DataListView";
import { getSafeThumbnailUrl } from "../../../utilities/mediaUtils";
import { getDataItemDescription, getDataItemName } from "../../../utilities/dataSourceAdapters";
import { isFulcrumMedia } from "../../../utilities/fulcrumLibService";

import type { ApiEndpoint, FlexibleDataItem, RelatedEntityItem } from "../../../types";
import { useSelectionSelector } from "../../../store/selection";

export interface DataGridViewRendererProps extends Omit<TableContainerProps, "onSelect"> {
  data: FlexibleDataItem[];
  onSelect: (item: FlexibleDataItem) => void;
  compact?: boolean;
  currentEndpoint?: ApiEndpoint | null;
  isPending?: boolean;
  isAllDataFinishLoading?: boolean;
  activeEntityId?: number | string;
  onLoadMore?: () => void;
  hasMoreToLoad?: boolean;
  isMoreLoading?: boolean;
  /** IDs of items whose geometry is within the current map viewport. When set, items outside the set are visually dimmed. */
  viewportVisibleIds?: Set<string | number>;
}

interface DataGridProps {
  data: FlexibleDataItem[];
  onSelect: (item: FlexibleDataItem) => void;
  compact?: boolean;
  currentEndpoint?: ApiEndpoint | null;
  searchTerm: string;
  hasMoreData?: boolean;
  onFilteredCountChange?: (filteredCount: number, hiddenDueToImages: number) => void;
  viewMode?: "cards" | "table";
  // Related items mode
  showRelatedItems?: boolean;
  relatedItemsData?: RelatedEntityItem[];
  onToggleRelatedItems?: () => void;
  selectedEntityName?: string;
  // Status text display
  onStatusText?: () => string;
  isPending?: boolean;
  showAllColumns?: boolean;
  onLoadMore?: () => void;
  isMoreLoading?: boolean;
  sx?: SxProps;
  viewportVisibleIds?: Set<string | number>;
}

const DataGrid = memo(
  ({
    data = [],
    onSelect,
    currentEndpoint,
    searchTerm,
    hasMoreData = false,
    onFilteredCountChange,
    viewMode = "cards",
    isPending,
    showAllColumns,
    onLoadMore,
    isMoreLoading,
    sx = {},
    viewportVisibleIds
  }: DataGridProps) => {
    const config = useConfig();
    const { enqueueSnackbar } = useSnackbar();
    const { selectedEntityId: activeEntityId } = useSelectionSelector();

    // Local filtering of data based on searchTerm using generalized getter functions
    const filteredData = useMemo(() => {
      let processedData = data;
      let hiddenDueToImages = 0;

      // First filter: hide cards without images if config setting is enabled
      if (config.hideCardsWithoutImages) {
        const originalCount = processedData.length;
        processedData = processedData.filter((item: any) => {
          const imageUrl = getSafeThumbnailUrl(item, currentEndpoint);
          return imageUrl && imageUrl.trim() !== "";
        });
        hiddenDueToImages = originalCount - processedData.length;
      }

      // Second filter: search term filtering
      if (searchTerm.trim()) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        processedData = processedData.filter((item: any) => {
          const itemName = getDataItemName(item, currentEndpoint || undefined);
          const itemDescription = getDataItemDescription(item, currentEndpoint || undefined);
          return itemName?.toLowerCase().includes(lowerSearchTerm) || itemDescription?.toLowerCase().includes(lowerSearchTerm);
        });
      }

      // Return both the filtered data and counts as an object
      return {
        data: processedData,
        filteredCount: processedData.length,
        hiddenDueToImages
      };
    }, [data, searchTerm, currentEndpoint, config.hideCardsWithoutImages]);

    // Use useEffect to notify parent about count changes (prevents render-phase state updates)
    useEffect(() => {
      onFilteredCountChange?.(filteredData.filteredCount, filteredData.hiddenDueToImages);
    }, [filteredData.filteredCount, filteredData.hiddenDueToImages, onFilteredCountChange]);

    const filteredItems = filteredData?.data;

    // Dynamic column generation for table view
    const generateTableColumns = useCallback(() => {
      if (showAllColumns) {
        // Show all available columns when expanded
        if (filteredItems?.length === 0) return [];

        const allKeys = new Set<string>();
        filteredItems.forEach((item) => {
          Object.keys(item).forEach((key) => {
            if (key !== "isAddNewItem" && key !== "geometry") {
              allKeys.add(key);
            }
          });
        });

        // Prioritize important columns
        // const priorityColumns = ["id", "created_at", "last_updated_at"];
        const priorityColumns = ["id", "description"];
        const remainingColumns = Array.from(allKeys).filter((key) => !priorityColumns.includes(key));

        return [...priorityColumns.filter((col) => allKeys.has(col)), ...remainingColumns.slice(0, 5)];
      } else {
        // Simple view: description
        // return ["created_at", "last_updated_at"];
        return ["description"];
      }
    }, [filteredItems, showAllColumns]);

    const tableColumns = generateTableColumns();

    // Memoize the onSelect handler
    const handleSelect = useCallback(
      (item: FlexibleDataItem) => {
        // Check if this is a Fulcrum photo that shouldn't be selectable
        // Enhanced detection: check for Fulcrum photo characteristics regardless of entityType
        const itemData = { ...item, ...(item as any).properties };

        const isFulcrumPhoto = isFulcrumMedia(itemData, currentEndpoint);
        if (isFulcrumPhoto) {
          console.log("[DataGrid] Preventing selection of Fulcrum photo - these are attachments, not standalone entities");
          // Show user-friendly message
          enqueueSnackbar("This item does not have its own properties", {
            variant: "info"
          });
          return;
        }

        onSelect(item);
      },
      [onSelect, currentEndpoint?.tableName, enqueueSnackbar, currentEndpoint]
    );

    const isDataFinishLoading = !hasMoreData && data.length > 0;

    const RenderComponent = viewMode === "table" ? DataListView : DataGridView;

    return (
      <Box sx={sx}>
        <RenderComponent
          columns={tableColumns}
          data={filteredData?.data}
          currentEndpoint={currentEndpoint}
          onSelect={handleSelect}
          isPending={isPending}
          isAllDataFinishLoading={isDataFinishLoading}
          activeEntityId={(isNaN(Number(activeEntityId)) ? activeEntityId : Number(activeEntityId)) || undefined}
          onLoadMore={onLoadMore}
          isMoreLoading={isMoreLoading}
          hasMoreToLoad={hasMoreData}
          viewportVisibleIds={viewportVisibleIds}
          sx={{ height: "100%" }}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if these props change
    return (
      prevProps.data === nextProps.data &&
      prevProps.isPending === nextProps.isPending &&
      prevProps.isMoreLoading === nextProps.isMoreLoading &&
      prevProps.compact === nextProps.compact &&
      prevProps.currentEndpoint?.tableName === nextProps.currentEndpoint?.tableName &&
      prevProps.onSelect === nextProps.onSelect &&
      prevProps.searchTerm === nextProps.searchTerm &&
      prevProps.hasMoreData === nextProps.hasMoreData &&
      prevProps.viewMode === nextProps.viewMode &&
      prevProps.showRelatedItems === nextProps.showRelatedItems &&
      prevProps.relatedItemsData === nextProps.relatedItemsData &&
      prevProps.selectedEntityName === nextProps.selectedEntityName
    );
  }
);

export default DataGrid;
